import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEO from "@/components/base/SEO";
import ScrollToTop from "@/components/base/ScrollToTop";
import { comics, categories } from "@/mocks/comics";
import ComicCard from "@/components/base/ComicCard";

const siteUrl = "https://hanmanzhijia.com";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;
  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 韩漫之家`,
    description: comic.description,
    author: {
      "@type": "Person",
      name: comic.author,
    },
    datePublished: "2024-01-15",
    dateModified: comic.updateTime,
    image: comic.cover,
    publisher: {
      "@type": "Organization",
      name: "韩漫之家",
      url: siteUrl,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/article/${comic.slug}`,
    },
  };

  return (
    <>
      <SEO
        title={`${comic.title}_${comic.author}韩国漫画免费阅读-韩漫之家`}
        description={`${comic.title}，作者${comic.author}，${comic.description.slice(0, 120)}。韩漫之家提供高清${categoryName}韩国漫画在线免费阅读，稳定可靠。`}
        keywords={`${comic.title},${comic.author},${categoryName}韩国漫画,韩漫在线免费阅读,韩漫之家`}
        canonicalUrl={`${siteUrl}/article/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        ogImageWidth="400"
        ogImageHeight="560"
        jsonLd={articleJsonLd}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: `${categoryName}漫画`, href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <article className="py-8 md:py-12 px-4 md:px-6 max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12">
            <div className="md:w-[280px] flex-shrink-0">
              <div className="rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - ${comic.author}韩国漫画封面`}
                  className="w-full aspect-[5/7] object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-6">作者：{comic.author}</p>

              <div className="flex flex-wrap gap-3 mb-6">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${comic.status === "已完结" ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"}`}>
                  {comic.status}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                  {categoryName}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-200 text-foreground-600 flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                  {comic.rating} 分
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-primary-500">{comic.chapters}</p>
                  <p className="text-xs text-foreground-500">总话数</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-primary-500">{comic.views}</p>
                  <p className="text-xs text-foreground-500">阅读量</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-accent-500">{comic.rating}</p>
                  <p className="text-xs text-foreground-500">评分</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-accent-500">{comic.updateTime}</p>
                  <p className="text-xs text-foreground-500">最近更新</p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="font-heading text-base font-semibold text-foreground-900 mb-3">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex gap-3">
                <button className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer shadow-sm">
                  开始阅读
                </button>
                <button className="px-8 py-3 rounded-full border border-primary-300 text-primary-600 font-medium text-sm hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer">
                  加入收藏
                </button>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <section className="mt-16">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}